import { create } from "zustand";

import { preserveIfEqual } from "@/shared/lib/state-change-guard";

export type CustomStatusEntry = {
  text: string | null;
  emoji: string | null;
  expiresAtUtc: string | null;
};

type PresenceState = {
  onlineUserIds: Set<string>;
  onlineCount: number;
  statusByUserId: Record<string, { status: string; expiresAtUtc: string | null }>;
  activityContextByUserId: Record<string, string | null>;
  customStatusByUserId: Record<string, CustomStatusEntry>;
  setAll: (ids: string[]) => void;
  setOnline: (id: string) => void;
  setOffline: (id: string) => void;
  setOnlineCount: (count: number) => void;
  setUserStatus: (id: string, status: string, expiresAtUtc: string | null) => void;
  setActivityContext: (userId: string, context: string | null) => void;
  setCustomStatus: (
    userId: string,
    text: string | null,
    emoji: string | null,
    expiresAtUtc: string | null,
  ) => void;
};

export const usePresenceStore = create<PresenceState>((set) => ({
  onlineUserIds: new Set<string>(),
  onlineCount: 0,
  statusByUserId: {},
  activityContextByUserId: {},
  customStatusByUserId: {},
  setAll: (ids) =>
    set((state) => {
      // KAN-236: onlineUserIds — Set (deepEqual его не сравнивает), поэтому сверяем явно.
      // Дедуплицируем ids через Set, чтобы дубликат в payload не маскировал удаление
      // (иначе size==length при разном составе → ложное "не изменилось").
      const next = new Set(ids);
      const unchanged =
        state.onlineUserIds.size === next.size &&
        [...next].every((id) => state.onlineUserIds.has(id));
      return unchanged
        ? {}
        : { onlineUserIds: next };
    }),
  setOnline: (id) =>
    set((state) => {
      const next = new Set(state.onlineUserIds);
      const wasNew = !next.has(id);
      next.add(id);
      return {
        onlineUserIds: next,
        onlineCount: wasNew ? state.onlineCount + 1 : state.onlineCount,
      };
    }),
  setOffline: (id) =>
    set((state) => {
      const next = new Set(state.onlineUserIds);
      const existed = next.delete(id);
      return {
        onlineUserIds: next,
        onlineCount: existed ? Math.max(0, state.onlineCount - 1) : state.onlineCount,
      };
    }),
  setOnlineCount: (count) => set({ onlineCount: count }),
  setUserStatus: (id, status, expiresAtUtc) =>
    set((state) => ({
      // KAN-236: не пересоздавать Record, если статус этого пользователя не изменился.
      statusByUserId: preserveIfEqual(state.statusByUserId, {
        ...state.statusByUserId,
        [id]: { status, expiresAtUtc },
      }),
    })),
  setActivityContext: (userId, context) =>
    set((state) => ({
      activityContextByUserId: preserveIfEqual(state.activityContextByUserId, {
        ...state.activityContextByUserId,
        [userId]: context,
      }),
    })),
  setCustomStatus: (userId, text, emoji, expiresAtUtc) =>
    set((state) => {
      // Keep a cleared entry so cached profile text cannot reappear after a presence event.
      const current = state.customStatusByUserId[userId];
      if (
        current?.text === text &&
        current.emoji === emoji &&
        current.expiresAtUtc === expiresAtUtc
      ) {
        return {};
      }
      return {
        customStatusByUserId: {
          ...state.customStatusByUserId,
          [userId]: { text, emoji, expiresAtUtc },
        },
      };
    }),
}));
